import HTMLTemplate from './template.html'
import './styles.scss'
import authClient from './../../modules/oktaAuth'
import User from '../../modules/user'
import ClientPortalAPIService from '../../services/api/ClientPortal/ApiService'
import MixPanelService from '../../services/MixPanelService'
import { debounce } from 'lodash'
import Sentry from '../../modules/SantryIO'
import { COUNTRY_DETAILS } from '../../data/country-details'

export const Settings = (() => {
  const vueComponent = {
    data() {
      return {
        userAirportCode: '',
        userMesaurementUnits: '',
        showSettings: false,
        activeSettingsSection: 'General', // Security, Cookies
        isRequestingResetPassword: false,
        hasRequestedResetPassword: false,
        resetPasswordAPIError: '',
        userEmail: '',
        showGeneralSettings: true,
        showNotificationsSection: true,
        showAirportCodeSetting: true,
        module: 'security',
        notificationSettings: {
          alertTypes: {
            medical: false,
            security: false,
            avsecBreaking: false,
            avsecStandard: false
          },
          severitiesMedical: {
            notice: false,
            advisory: false,
            specialAdvisory: false,
            evacuationWarning: false,
            evacuationStandby: false,
            nonEssentialEvacuation: false,
            fullEvacuation: false
          },
          severitiesSecurity: {
            notice: false,
            advisory: false,
            specialAdvisory: false,
            evacuationWarning: false,
            evacuationStandby: false,
            nonEssentialEvacuation: false,
            fullEvacuation: false
          },
          severitiesAvsecBreaking: {
            notice: false,
            advisory: false,
            specialAdvisory: false
          },
          severitiesAvsecStandard: {
            notice: false,
            advisory: false,
            specialAdvisory: false
          },
          selectedCountries: [] // Will store ISO codes of selected countries
        },
        // Accordion open/close states
        medicalAccordionOpen: false,
        securityAccordionOpen: false,
        avsecBreakingAccordionOpen: false,
        avsecStandardAccordionOpen: false,
        // Severity mappings
        severityMapping: {
          advisory: 'advisory',
          fullEvacuation: 'evacuate-full-evacuation',
          nonEssentialEvacuation: 'evacuate-non-essential-staff',
          notice: 'notice',
          specialAdvisory: 'special-advisory',
          evacuationStandby: 'stand-by',
          evacuationWarning: 'warning'
        },
        // Countries and regions data
        regionData: [],
        isDropdownOpen: false,
        searchQuery: '',
        shouldOpenUp: false,
        scrollTimeout: null,
        lastScrollPosition: 0,
        DROPDOWN_CONSTANTS: {
          DEBOUNCE_DELAY: 50,
          SPACE_BUFFER: 20,
          ESTIMATED_ITEM_HEIGHT: 40,
          ESTIMATED_HEADER_HEIGHT: 50
        },
        showMedicalAlert: false, // visibility flags for alert types
        showSecurityAlert: false,
        showAvsecAlert: false,
        alertConfig: {
          medical: {
            severities: ['notice', 'advisory', 'specialAdvisory'],
            accordionKey: 'medicalAccordionOpen',
            alertTypeKey: 'medical',
            severitiesKey: 'severitiesMedical'
          },
          security: {
            severities: ['notice', 'advisory', 'specialAdvisory', 'evacuationWarning', 'evacuationStandby', 'nonEssentialEvacuation', 'fullEvacuation'],
            accordionKey: 'securityAccordionOpen',
            alertTypeKey: 'security',
            severitiesKey: 'severitiesSecurity'
          },
          'avsec-breaking': {
            severities: ['notice', 'advisory', 'specialAdvisory'],
            accordionKey: 'avsecBreakingAccordionOpen',
            alertTypeKey: 'avsecBreaking',
            severitiesKey: 'severitiesAvsecBreaking'
          },
          'avsec-standard': {
            severities: ['notice', 'advisory', 'specialAdvisory'],
            accordionKey: 'avsecStandardAccordionOpen',
            alertTypeKey: 'avsecStandard',
            severitiesKey: 'severitiesAvsecStandard'
          }
        },
        severityDisplayLabels: {
          notice: 'Notice',
          advisory: 'Advisory',
          specialAdvisory: 'Special Advisory',
          evacuationWarning: 'Evacuation Warning',
          evacuationStandby: 'Evacuation Standby',
          nonEssentialEvacuation: 'Non-essential Evacuation',
          fullEvacuation: 'Full Evacuation'
        }
      }
    },
    created() {
      User.getUser().then(user => {
        this.userAirportCode = user.settings.airportCode
        this.userMesaurementUnits = user.settings.mesaurementUnits
        this.userEmail = user.email
        this.getUserAlertPreferences()
      })
      User.getUserPermissions().then(permissions => {
        this.showNotificationsSection = !(permissions?.length === 1 && permissions?.includes('MediTrack'))

        // This code is added by adam bugfix/ms-1541-meditrack-notification-display-issue (pull request #829). For now I have commented it as we have 
        // already cover this case in above code and everything is working fine.
        // this.showNotificationsSection = permissions?.includes('MediTrack') && !permissions?.includes('luxury_yachting') ? false : true
        const lowerPerms = permissions.map(p => p.toLowerCase())

        if (lowerPerms.includes('luxury_yachting') || lowerPerms.includes('MediTrack')) {
          this.showAvsecAlert = false
        }

      })
      // Initialize region data from COUNTRY_DETAILS
      this.initializeRegionData()
    },
    computed: {
      medicalSeveritiesSelectedCount() {
        return Object.values(this.notificationSettings.severitiesMedical).filter(Boolean).length
      },
      securitySeveritiesSelectedCount() {
        return Object.values(this.notificationSettings.severitiesSecurity).filter(Boolean).length
      },
      avsecBreakingSeveritiesSelectedCount() {
        return Object.values(this.notificationSettings.severitiesAvsecBreaking).filter(Boolean).length
      },
      avsecStandardSeveritiesSelectedCount() {
        return Object.values(this.notificationSettings.severitiesAvsecStandard).filter(Boolean).length
      },
      sortedRegions() {
        const query = this.searchQuery.toLowerCase()
        return [...this.regionData]
          .filter(region => !query || region.regionName.toLowerCase().includes(query))
          .sort((a, b) => a.regionName.localeCompare(b.regionName))
      },
      sortedCountries() {
        let allCountries = this.regionData.flatMap(region =>
          region.countries.map(country => ({
            ...country,
            regionName: region.regionName
          }))
        )
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase()
          allCountries = allCountries.filter(country => country.name.toLowerCase().includes(query))
        }
        return allCountries.sort((a, b) => a.name.localeCompare(b.name))
      }
    },
    methods: {
      changeModuleType(moduleType) {
        this.module = moduleType
      },
      // ===== User Settings Methods =====
      getUserAlertPreferences() {
        ClientPortalAPIService.getUserAlertSubscriptionPreferences()
          .then(response => {
            const data = response

            this.showMedicalAlert = data.allowedAlertTypes.includes('medical')
            this.showSecurityAlert = data.allowedAlertTypes.includes('security')
            this.showAvsecAlert = data.allowedAlertTypes.includes('avsec')

            // Update alert types
            this.notificationSettings.alertTypes = {
              medical: data.medical || false,
              security: data.security || false,
              avsecBreaking: data.avsec?.includes('breaking-alert') || false,
              avsecStandard: data.avsec?.includes('standard-alert') || false
            }

            // Update severities with direct mapping
            this.notificationSettings.severitiesMedical = {
              notice: data.medicalSeverities?.includes('notice') || false,
              advisory: data.medicalSeverities?.includes('advisory') || false,
              specialAdvisory: data.medicalSeverities?.includes('special-advisory') || false
            }

            this.notificationSettings.severitiesSecurity = {
              notice: data.securitySeverities?.includes('notice') || false,
              advisory: data.securitySeverities?.includes('advisory') || false,
              specialAdvisory: data.securitySeverities?.includes('special-advisory') || false,
              evacuationWarning: data.securitySeverities?.includes('warning') || false,
              evacuationStandby: data.securitySeverities?.includes('stand-by') || false,
              nonEssentialEvacuation: data.securitySeverities?.includes('evacuate-non-essential-staff') || false,
              fullEvacuation: data.securitySeverities?.includes('evacuate-full-evacuation') || false
            }

            // Update AVSEC severities separately
            this.notificationSettings.severitiesAvsecBreaking = {
              notice: data.breakingSeverities?.includes('notice') || false,
              advisory: data.breakingSeverities?.includes('advisory') || false,
              specialAdvisory: data.breakingSeverities?.includes('special-advisory') || false
            }

            this.notificationSettings.severitiesAvsecStandard = {
              notice: data.standardSeverities?.includes('notice') || false,
              advisory: data.standardSeverities?.includes('advisory') || false,
              specialAdvisory: data.standardSeverities?.includes('special-advisory') || false
            }

            // Update selected countries
            this.notificationSettings.selectedCountries = data.countries || []

            // Update region selections
            this.regionData.forEach(region => {
              region.countries.forEach(country => {
                country.isSelected = this.notificationSettings.selectedCountries.includes(country.code)
              })
              region.isSelected = region.countries.every(c => c.isSelected)
            })
          })
          .catch(error => {
            console.error('Error fetching alert preferences:', error)
            Sentry.captureException(error)
          })
      },
      getCountryName(countryCode) {
        const country = COUNTRY_DETAILS.find(c => c.ISO_3166_1_ALPHA_3 === countryCode)
        return country ? country.name : countryCode
      },
      // Initialize region and dropdown data
      initializeRegionData() {
        // Group countries by region
        const regionMap = new Map()
        COUNTRY_DETAILS.forEach(country => {
          if (!regionMap.has(country.region)) {
            regionMap.set(country.region, [])
          }
          if (country.ISO_3166_1_ALPHA_3) {
            regionMap.get(country.region).push({
              name: country.name,
              code: country.ISO_3166_1_ALPHA_3,
              isSelected: false
            })
          }
        })

        // Convert to array format
        this.regionData = Array.from(regionMap.entries()).map(([regionName, countries]) => ({
          regionName,
          isSelected: false,
          countries: countries.sort((a, b) => a.name.localeCompare(b.name))
        }))
      },
      // ===== Notification Settings Methods =====
      isGroupFullySelected(regionName) {
        const region = this.regionData.find(r => r.regionName === regionName)
        return region && region.countries.every(country =>
          this.notificationSettings.selectedCountries.includes(country.code)
        )
      },
      toggleGroupSelection(regionName) {
        const region = this.regionData.find(r => r.regionName === regionName)
        if (!region) return
        const shouldSelect = !this.isGroupFullySelected(regionName)

        region.countries.forEach(country => {
          country.isSelected = shouldSelect
          if (shouldSelect) {
            if (!this.notificationSettings.selectedCountries.includes(country.code)) {
              this.notificationSettings.selectedCountries.push(country.code)
            }
          } else {
            this.notificationSettings.selectedCountries = this.notificationSettings.selectedCountries.filter(code => code !== country.code)
          }
        })
        region.isSelected = shouldSelect
        this.debounceUpdateNotificationSettings()
      },
      isCountrySelected(countryCode) {
        return this.notificationSettings.selectedCountries.includes(countryCode)
      },
      toggleCountrySelection(country) {
        const idx = this.notificationSettings.selectedCountries.indexOf(country.code)
        if (idx === -1) {
          this.notificationSettings.selectedCountries.push(country.code)
          this.updateCountrySelection(country.code, true)
        } else {
          this.notificationSettings.selectedCountries.splice(idx, 1)
          this.updateCountrySelection(country.code, false)
        }
        this.checkRegionSelectionStatus(country.regionName)
        this.debounceUpdateNotificationSettings()
      },
      updateCountrySelection(countryCode, isSelected) {
        for (const region of this.regionData) {
          const country = region.countries.find(c => c.code === countryCode)
          if (country) {
            country.isSelected = isSelected
            break
          }
        }
      },
      checkRegionSelectionStatus(regionName) {
        const region = this.regionData.find(r => r.regionName === regionName)
        if (region) {
          region.isSelected = region.countries.every(c => c.isSelected)
        }
      },
      clearSelectedCountries() {
        this.notificationSettings.selectedCountries = []
        this.regionData.forEach(region => {
          region.countries.forEach(country => (country.isSelected = false))
          region.isSelected = false
        })
        this.debounceUpdateNotificationSettings()
      },
      // ===== Dropdown Methods =====
      toggleDropdown() {
        if (!this.isDropdownOpen) {
          this.calculateDropdownPosition()
          this.isDropdownOpen = true
          this.searchQuery = ''
        } else {
          this.isDropdownOpen = false
        }
      },
      onSearch() {
        if (!this.isDropdownOpen) {
          this.toggleDropdown()
        }
      },
      calculateDropdownPosition(scrollDirection) {
        const dropdownHeader = this.$refs.dropdownHeader
        const settingsContent = this.$refs.settingsContent

        if (!dropdownHeader || !settingsContent) return

        const headerRect = dropdownHeader.getBoundingClientRect()
        const contentRect = settingsContent.getBoundingClientRect()

        const spaceBelow = contentRect.bottom - headerRect.bottom
        const spaceAbove = headerRect.top - contentRect.top

        const estimatedContentHeight = this.calculateEstimatedContentHeight()

        this.updateDropdownDirection(spaceAbove, spaceBelow, estimatedContentHeight)
      },
      calculateEstimatedContentHeight() {
        const { ESTIMATED_ITEM_HEIGHT, ESTIMATED_HEADER_HEIGHT } = this.DROPDOWN_CONSTANTS
        return (this.regionData.length + this.sortedCountries.length) * ESTIMATED_ITEM_HEIGHT + ESTIMATED_HEADER_HEIGHT
      },
      updateDropdownDirection(spaceAbove, spaceBelow, estimatedContentHeight) {
        const { SPACE_BUFFER } = this.DROPDOWN_CONSTANTS

        if (this.shouldOpenUp) {
          if (spaceBelow > spaceAbove + SPACE_BUFFER) {
            this.shouldOpenUp = false
          }
        } else {
          if (spaceAbove > spaceBelow + SPACE_BUFFER) {
            this.shouldOpenUp = true
          }
        }
      },
      handleClickOutside(event) {
        const dropdown = event.target.closest('.custom-dropdown')
        if (!dropdown && this.isDropdownOpen) {
          this.isDropdownOpen = false
        }
      },
      handleResize: debounce(function () {
        if (this.isDropdownOpen) {
          this.calculateDropdownPosition()
        }
      }, 100),
      handleScroll(event) {
        if (!this.isDropdownOpen) return

        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout)
        }

        const currentScroll = event.target.scrollTop
        const scrollDirection = currentScroll > this.lastScrollPosition ? 'down' : 'up'
        this.lastScrollPosition = currentScroll

        this.scrollTimeout = setTimeout(() => {
          this.calculateDropdownPosition(scrollDirection)
        }, this.DROPDOWN_CONSTANTS.DEBOUNCE_DELAY)
      },
      // Debounced update: waits 1 second after user stops clicking before syncing to server
      // Local state updates immediately via v-model binding, API sync happens after debounce
      debounceUpdateNotificationSettings: debounce(function () {
        this.updateNotificationSettings()
      }, 1000),
      updateNotificationSettings() {
        // Sync local state to server (local state already updated immediately via v-model)
        // Transform the data to match the API requirements
        const requestData = {
          medical: this.notificationSettings.alertTypes.medical,
          security: this.notificationSettings.alertTypes.security,
          avsec: [],
          MedicalSeverities: [],
          SecuritySeverities: [],
          BreakingSeverities: [],
          StandardSeverities: [],
          countries: this.notificationSettings.selectedCountries,
          regions: []
        }

        // Add AVSEC alerts if any are selected
        if (this.notificationSettings.alertTypes.avsecBreaking) {
          requestData.avsec.push('breaking-alert')
        }
        if (this.notificationSettings.alertTypes.avsecStandard) {
          requestData.avsec.push('standard-alert')
        }

        // Add selected severities with mapping for each type
        Object.entries(this.notificationSettings.severitiesMedical).forEach(([key, value]) => {
          if (value) {
            requestData.MedicalSeverities.push(this.severityMapping[key])
          }
        })

        Object.entries(this.notificationSettings.severitiesSecurity).forEach(([key, value]) => {
          if (value) {
            requestData.SecuritySeverities.push(this.severityMapping[key])
          }
        })

        Object.entries(this.notificationSettings.severitiesAvsecBreaking).forEach(([key, value]) => {
          if (value) {
            requestData.BreakingSeverities.push(this.severityMapping[key])
          }
        })

        Object.entries(this.notificationSettings.severitiesAvsecStandard).forEach(([key, value]) => {
          if (value) {
            requestData.StandardSeverities.push(this.severityMapping[key])
          }
        })

        // Add selected regions
        this.regionData.forEach(region => {
          if (region.isSelected) {
            requestData.regions.push(region.regionName)
          }
        })

        // Call the API to sync local state to server
        // Using optimistic updates: local state already updated, no refetch on success
        ClientPortalAPIService.updateUserAlertSubscriptionPreferences(requestData)
          .then(() => {
            // Success: local state is already correct, no need to refetch
            // This prevents stale responses from overwriting user's current state
          })
          .catch(error => {
            console.error('Error updating alert preferences:', error)
            Sentry.captureException(error)
            // On error, refetch to sync with server state
            this.getUserAlertPreferences()
          })
      },
      // ===== Feedback Methods =====
      openExternalFeedbackForm() {
        window.open('https://www.medaire.com/techsupport', '_blank')
      },
      resetPassword() {
        this.isRequestingResetPassword = true
        this.resetPasswordAPIError = ''

        ClientPortalAPIService.resetPassword()
          .then(() => {
            this.isRequestingResetPassword = false
            this.hasRequestedResetPassword = true

            window.setTimeout(() => authClient.signOut(), 1000)
          })
          .catch(error => {
            Sentry.captureException(error)
            this.resetPasswordAPIError = error
          })
          .then(() => {
            this.isRequestingResetPassword = false
          })
      },
      // Helper method to open accordion
      openAccordion(accordionId) {
        this.$nextTick(() => {
          const accordion = document.querySelector(`.accordion:has(#${accordionId})`)
          if (accordion) {
            accordion.classList.add('show-content')
            const contentContainer = accordion.querySelector('.accordion__content-container')
            const content = accordion.querySelector('.accordion__content')
            if (contentContainer && content) {
              contentContainer.style.height = `${content.offsetHeight}px`
            }
            // Sync accordion state flag
            const type = accordionId.replace('alert-', '')
            const config = this.alertConfig[type]
            if (config) {
              this[config.accordionKey] = true
            }
          }
        })
      },
      // Alert type change handler
      // Note: Local state updates immediately via v-model binding on the checkbox
      // This handler manages accordion state and triggers debounced API sync
      onAlertTypeChange(event) {
        const type = event.target.id.replace('alert-', '')
        const config = this.alertConfig[type]
        if (config) {
          const checked = this.notificationSettings.alertTypes[config.alertTypeKey]
          // Update severities locally
          this.updateSeverities(config.severitiesKey, config.severities, checked)
          // Open accordion if checked
          if (checked) {
            this[config.accordionKey] = true
            this.openAccordion(`alert-${type}`)
          }
          // Trigger debounced API sync (waits until user stops clicking)
          this.debounceUpdateNotificationSettings()
        }
      },
      // Helper method to update severities
      updateSeverities(severitiesKey, severities, checked) {
        severities.forEach(severity => {
          this.notificationSettings[severitiesKey][severity] = checked
        })
      },
      // Optimized accordion toggle
      toggleAccordion(event) {
        // Ignore clicks on checkboxes, labels, or checkbox containers
        const target = event.target
        if (target.tagName === 'INPUT' && target.type === 'checkbox') {
          return
        }
        if (target.tagName === 'LABEL' || target.closest('.checkbox')) {
          return
        }

        const accordion = event.target.closest('.accordion')
        if (!accordion) return

        const contentContainer = accordion.querySelector('.accordion__content-container')
        const content = accordion.querySelector('.accordion__content')
        const contentHeight = content.offsetHeight

        const isOpen = accordion.classList.contains('show-content')
        
        // Update accordion state flags based on which accordion is being toggled
        const accordionId = accordion.querySelector('input[type="checkbox"]')?.id
        if (accordionId) {
          const type = accordionId.replace('alert-', '')
          const config = this.alertConfig[type]
          if (config) {
            this[config.accordionKey] = !isOpen
          }
        }

        if (isOpen) {
          accordion.classList.remove('show-content')
          contentContainer.style.height = '0px'
        } else {
          accordion.classList.add('show-content')
          contentContainer.style.height = `${contentHeight}px`
        }
      },
      // Severity change handlers
      // Local state updates immediately via v-model, API sync happens after debounce
      onAvsecBreakingSeverityChange() {
        this.notificationSettings.alertTypes.avsecBreaking = Object.values(this.notificationSettings.severitiesAvsecBreaking).some(value => value)
        this.debounceUpdateNotificationSettings()
      },
      onAvsecStandardSeverityChange() {
        this.notificationSettings.alertTypes.avsecStandard = Object.values(this.notificationSettings.severitiesAvsecStandard).some(value => value)
        this.debounceUpdateNotificationSettings()
      },
      onMedicalSeverityChange() {
        this.notificationSettings.alertTypes.medical = Object.values(this.notificationSettings.severitiesMedical).some(value => value)
        this.debounceUpdateNotificationSettings()
      },
      onSecuritySeverityChange() {
        this.notificationSettings.alertTypes.security = Object.values(this.notificationSettings.severitiesSecurity).some(value => value)
        this.debounceUpdateNotificationSettings()
      },
      debounceUpdateUserSettings: debounce(function () {
        this.updateUserSettings()
      }, 1000),
      updateUserSettings() {
        User.getLocalUserSettings().then(settings => {
          let updatedUserSettings = settings
          updatedUserSettings.airportCode = this.userAirportCode
          updatedUserSettings.mesaurementUnits = this.userMesaurementUnits

          User.updateUserSettings(updatedUserSettings)
        })
      },
      restartSettingsState() {
        if (this.showGeneralSettings) {
          this.activeSettingsSection = 'General'
        } else {
          this.activeSettingsSection = 'Security'
        }
      }
    },
    watch: {
      showSettings: function () {
        if (!this.showSettings) this.restartSettingsState()
      },
      activeSettingsSection: function (newSection) {
        // Sync data from server when user opens Notifications tab
        if (newSection === 'Notifications') {
          this.getUserAlertPreferences()
        }
      }
    },
    mounted() {
      document.addEventListener('click', this.handleClickOutside)
      window.addEventListener('resize', this.handleResize)

      if (this.$refs.settingsContent) {
        this.$refs.settingsContent.addEventListener('scroll', this.handleScroll)
      }
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside)
      window.removeEventListener('resize', this.handleResize)

      if (this.$refs.settingsContent) {
        this.$refs.settingsContent.removeEventListener('scroll', this.handleScroll)
      }

      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout)
        this.scrollTimeout = null
      }
    },
    template: HTMLTemplate
  }

  const DOMElement = document.getElementById('vue-settings')
  if (DOMElement === null) {
    return null
  } else {
    return Vue.createApp(vueComponent).mount(DOMElement)
  }
})()
